import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LogOut, Inbox, Users, LineChart, Megaphone, Loader2,
  Plus, Trash2, ChevronRight, CheckCircle2, Package, Truck, Undo2,
  MapPin, Phone, Copy, Heart, ShoppingBag,
} from 'lucide-react';
import { adminApi, logout, BOTHY_PATH } from '@/lib/bothyAuth';
import BrandLogo from '@/components/BrandLogo';
import SanityCard from '@/components/SanityCard';
import ReplyTemplateMenu from '@/components/ReplyTemplateMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};
const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};
const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n || 0);

// ──────────────────────────────────────────────────────────────
// Dashboard shell
// ──────────────────────────────────────────────────────────────
export default function BothyDashboard({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('enquiries');

  const signOut = async () => {
    await logout();
    navigate(BOTHY_PATH, { replace: true });
  };

  return (
    <div data-testid="bothy-dashboard" className="min-h-screen bg-[var(--brand-parchment)]">
      <header className="bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandLogo size={48} />
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--brand-gold)]">The Bothy</div>
              <div className="font-display text-xl">Founder’s Workshop</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="w-9 h-9 rounded-full border border-[var(--brand-gold)]/40"
              />
            )}
            <div className="hidden sm:block text-right text-sm leading-tight">
              <div className="font-semibold">{user?.name}</div>
              <div className="text-[var(--brand-parchment)]/70 text-xs">{user?.email}</div>
            </div>
            <button
              data-testid="bothy-signout"
              onClick={signOut}
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] border border-[var(--brand-parchment)]/40 hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] px-3 py-2 transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <nav className="relative max-w-7xl mx-auto px-6 lg:px-10 flex gap-2 overflow-x-auto">
          <TabBtn id="enquiries" active={tab} setTab={setTab} icon={Inbox} label="Enquiries" />
          <TabBtn id="orders" active={tab} setTab={setTab} icon={Package} label="Orders" />
          <TabBtn id="subscribers" active={tab} setTab={setTab} icon={Users} label="Subscribers" />
          <TabBtn id="wishlist" active={tab} setTab={setTab} icon={Heart} label="Most-Wished-For" />
          <TabBtn id="bundles" active={tab} setTab={setTab} icon={ShoppingBag} label="Bundles" />
          <TabBtn id="forecast" active={tab} setTab={setTab} icon={LineChart} label="Sales Forecast" />
          <TabBtn id="marketing" active={tab} setTab={setTab} icon={Megaphone} label="Marketing" />
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <SanityCard />
        {tab === 'enquiries' && <EnquiriesPanel />}
        {tab === 'orders' && <OrdersPanel />}
        {tab === 'subscribers' && <SubscribersPanel />}
        {tab === 'wishlist' && <WishlistPanel />}
        {tab === 'bundles' && <BundlesPanel />}
        {tab === 'forecast' && <ForecastPanel />}
        {tab === 'marketing' && <MarketingPanel />}
      </main>
    </div>
  );
}

function TabBtn({ id, active, setTab, icon: Icon, label }) {
  const isActive = active === id;
  return (
    <button
      data-testid={`bothy-tab-${id}`}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 text-sm font-semibold px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
        isActive
          ? 'border-[var(--brand-gold)] text-[var(--brand-gold)]'
          : 'border-transparent text-[var(--brand-parchment)]/70 hover:text-[var(--brand-parchment)]'
      }`}
    >
      <Icon size={15} /> {label}
    </button>
  );
}

const PanelLoader = () => (
  <div className="py-20 grid place-items-center">
    <Loader2 className="animate-spin text-[var(--brand-olive)]" size={26} />
  </div>
);

const EmptyState = ({ icon: Icon, title, body }) => (
  <div className="text-center py-16 text-[var(--brand-muted)]">
    <Icon size={32} className="mx-auto text-[var(--brand-olive)]" strokeWidth={1.3} />
    <div className="mt-3 font-display text-2xl text-[var(--brand-forest)]">{title}</div>
    <p className="mt-2 text-sm max-w-sm mx-auto leading-relaxed">{body}</p>
  </div>
);

// ──────────────────────────────────────────────────────────────
// Enquiries
// ──────────────────────────────────────────────────────────────
function EnquiriesPanel() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => { adminApi.enquiries().then(setRows).catch(() => setRows([])); }, []);

  if (rows === null) return <PanelLoader />;
  if (!rows.length) {
    return <EmptyState icon={Inbox} title="No enquiries yet"
      body="When customers submit the contact form, you’ll see them here — newest first." />;
  }
  return (
    <div data-testid="enquiries-panel" className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 space-y-2">
        <h2 className="font-display text-3xl text-[var(--brand-forest)] mb-4">{rows.length} enquiries</h2>
        {rows.map((r) => (
          <button
            key={r.id}
            data-testid={`enquiry-row-${r.id}`}
            onClick={() => setOpen(r)}
            className={`w-full text-left p-4 border transition-colors ${
              open?.id === r.id
                ? 'bg-[var(--brand-forest)] text-[var(--brand-parchment)] border-[var(--brand-forest)]'
                : 'bg-white border-[var(--brand-stone)] hover:border-[var(--brand-olive)]'
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold">{r.name}</span>
              <span className="text-xs opacity-75">{fmtDateTime(r.created_at)}</span>
            </div>
            <div className="text-sm mt-1 truncate opacity-90">{r.subject}</div>
            {r.interest && (
              <span className="inline-block mt-2 text-[10px] uppercase tracking-[0.18em] bg-[var(--brand-gold)]/20 text-[var(--brand-forest)] px-2 py-0.5">
                {r.interest}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="lg:col-span-7">
        {open ? (
          <div data-testid="enquiry-detail" className="bg-white border border-[var(--brand-stone)] p-6 lg:p-8 sticky top-6">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-display text-2xl text-[var(--brand-forest)]">{open.name}</h3>
              <span className="text-xs text-[var(--brand-muted)]">{fmtDateTime(open.created_at)}</span>
            </div>
            <div className="mt-1 text-sm">
              <a href={`mailto:${open.email}`} className="link-expand text-[var(--brand-olive)] font-semibold">{open.email}</a>
              {open.phone && <span className="text-[var(--brand-muted)]"> · {open.phone}</span>}
            </div>
            <div className="mt-5 text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Subject</div>
            <div className="font-display text-xl text-[var(--brand-forest)]">{open.subject}</div>
            {open.interest && (
              <div className="mt-2"><span className="text-[10px] uppercase tracking-[0.18em] bg-[var(--brand-gold)]/30 text-[var(--brand-forest)] px-2 py-0.5">{open.interest}</span></div>
            )}
            <div className="mt-5 text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">Message</div>
            <p className="text-base leading-relaxed whitespace-pre-wrap">{open.message}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={`mailto:${open.email}?subject=Re:%20${encodeURIComponent(open.subject)}`}
                className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-none"
              >
                Blank reply <ChevronRight size={14} />
              </a>
              <ReplyTemplateMenu enquiry={open} />
            </div>
          </div>
        ) : (
          <div className="text-[var(--brand-muted)] text-sm border border-dashed border-[var(--brand-stone)] p-8">
            Select an enquiry on the left to read it in full.
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Orders (drop-ship fulfilment)
// ──────────────────────────────────────────────────────────────

// Compact shipping-address block for each order row. Shows the customer's
// name + UK address as captured by Stripe Checkout. The "Copy" button gives
// the founder a one-click way to grab the address for the Eahora handoff.
function ShippingBlock({ order }) {
  const addr = order.shipping_address || {};
  const hasAddress = addr.line1 || addr.postal_code;
  const [copied, setCopied] = useState(false);

  if (!hasAddress && !order.shipping_name && !order.customer_phone) return null;

  const lines = [
    order.shipping_name,
    addr.line1,
    addr.line2,
    addr.city,
    addr.state,
    addr.postal_code,
    addr.country,
  ].filter(Boolean);

  const copy = async () => {
    try {
      const text = lines.join('\n');
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Older browsers / iframe restrictions — silently no-op
    }
  };

  return (
    <div
      data-testid={`order-shipping-${order.id}`}
      className="mt-3 bg-[var(--brand-parchment)] border-l-2 border-[var(--brand-gold)] px-4 py-3 text-sm"
    >
      <div className="flex items-start gap-2 flex-wrap">
        <MapPin
          size={14}
          className="text-[var(--brand-olive)] mt-1 shrink-0"
          strokeWidth={2}
        />
        <div className="flex-1 min-w-0">
          {hasAddress ? (
            <address className="not-italic text-[var(--brand-ink)] leading-relaxed">
              {order.shipping_name && (
                <div className="font-semibold">{order.shipping_name}</div>
              )}
              {addr.line1 && <div>{addr.line1}</div>}
              {addr.line2 && <div>{addr.line2}</div>}
              {(addr.city || addr.state) && (
                <div>
                  {[addr.city, addr.state].filter(Boolean).join(', ')}
                </div>
              )}
              {addr.postal_code && (
                <div className="font-mono text-xs uppercase tracking-wide">
                  {addr.postal_code}
                  {addr.country && ` · ${addr.country}`}
                </div>
              )}
            </address>
          ) : (
            <p className="text-[var(--brand-muted)] italic text-xs">
              No shipping address on file (older order — created before address
              capture was enabled).
            </p>
          )}
          {order.customer_phone && (
            <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-[var(--brand-muted)]">
              <Phone size={11} strokeWidth={2} />
              <a
                href={`tel:${order.customer_phone}`}
                className="link-expand text-[var(--brand-olive)]"
              >
                {order.customer_phone}
              </a>
            </div>
          )}
        </div>
        {hasAddress && (
          <button
            data-testid={`order-copy-address-${order.id}`}
            onClick={copy}
            className="text-[10px] uppercase tracking-[0.16em] px-2 py-1 border border-[var(--brand-stone)] hover:border-[var(--brand-olive)] hover:bg-white transition-colors inline-flex items-center gap-1 shrink-0"
            title="Copy address — handy for pasting into the Eahora chat"
          >
            {copied ? (
              <>
                <CheckCircle2 size={11} className="text-[var(--brand-olive)]" /> Copied
              </>
            ) : (
              <>
                <Copy size={11} /> Copy
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

const ORDER_STATES = {
  paid: {
    label: 'Awaiting forward',
    badgeClass: 'bg-[var(--brand-gold)]/30 text-[var(--brand-forest)]',
  },
  forwarded: {
    label: 'Forwarded to Eahora',
    badgeClass: 'bg-[var(--brand-olive)]/30 text-[var(--brand-forest)]',
  },
  dispatched: {
    label: 'Dispatched',
    badgeClass: 'bg-[var(--brand-forest)] text-[var(--brand-parchment)]',
  },
};

function OrdersPanel() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [reminderData, setReminderData] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderError, setReminderError] = useState(null);
  const [reminderResult, setReminderResult] = useState(null);

  const reload = () =>
    adminApi.orders().then(setRows).catch(() => setRows([]));
  useEffect(() => { reload(); }, []);

  const loadAbandoned = async ({ preserveResult = false } = {}) => {
    setReminderError(null);
    if (!preserveResult) setReminderResult(null);
    try {
      const r = await axios.get(`${API}/admin/abandoned-checkouts`, { withCredentials: true });
      setReminderData(r.data);
    } catch (e) {
      setReminderError(e?.response?.data?.detail || 'Could not load abandoned checkouts');
      setReminderData({ candidates: [], count: 0 });
    }
  };

  const sendReminders = async () => {
    setReminderBusy(true);
    setReminderError(null);
    try {
      const r = await axios.post(`${API}/admin/abandoned-checkouts/send`, {}, { withCredentials: true });
      setReminderResult(r.data);
      // Refresh preview so sent rows disappear, but keep the result summary visible.
      await loadAbandoned({ preserveResult: true });
    } catch (e) {
      setReminderError(e?.response?.data?.detail || 'Send failed');
    } finally {
      setReminderBusy(false);
    }
  };

  const action = async (id, fn) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (filter === 'all') return rows;
    return rows.filter((r) => (r.fulfilment_status || 'paid') === filter);
  }, [rows, filter]);

  const counts = useMemo(() => {
    if (!rows) return { all: 0, paid: 0, forwarded: 0, dispatched: 0 };
    const c = { all: rows.length, paid: 0, forwarded: 0, dispatched: 0 };
    rows.forEach((r) => {
      const s = r.fulfilment_status || 'paid';
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [rows]);

  if (rows === null) return <PanelLoader />;

  return (
    <div data-testid="orders-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl text-[var(--brand-forest)]">Orders</h2>
          <p className="text-[var(--brand-muted)] text-sm mt-1">
            Drop-ship fulfilment loop. Mark each paid order as forwarded to Eahora,
            then dispatched once the factory confirms it&rsquo;s shipped.
          </p>
        </div>
        <div className="flex gap-2 text-xs uppercase tracking-[0.18em]">
          {[
            ['all', `All · ${counts.all}`],
            ['paid', `Awaiting · ${counts.paid}`],
            ['forwarded', `Forwarded · ${counts.forwarded}`],
            ['dispatched', `Dispatched · ${counts.dispatched}`],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`orders-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-2 border transition-colors ${
                filter === k
                  ? 'bg-[var(--brand-forest)] text-[var(--brand-parchment)] border-[var(--brand-forest)]'
                  : 'bg-white border-[var(--brand-stone)] hover:border-[var(--brand-olive)]'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            data-testid="orders-open-abandoned"
            onClick={loadAbandoned}
            className="px-3 py-2 border border-[var(--brand-stone)] bg-[var(--brand-parchment-warm)] hover:border-[var(--brand-gold)] transition-colors"
            title="Abandoned checkouts (initiated 24h+ ago, no payment)"
          >
            Abandoned
          </button>
        </div>
      </div>

      {reminderData !== null && (
        <div
          data-testid="orders-abandoned-panel"
          className="mb-6 bg-[var(--brand-parchment-warm)] border border-[var(--brand-stone)] p-5"
        >
          <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
            <div>
              <div className="font-eyebrow mb-1">Abandoned checkouts</div>
              <div className="font-display text-lg text-[var(--brand-forest)]">
                {reminderData.count} customer{reminderData.count === 1 ? '' : 's'} started but didn&rsquo;t finish
              </div>
              <p className="text-xs text-[var(--brand-muted)] mt-1">
                Older than 24h, has an email, no matching paid transaction, not yet reminded.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="orders-abandoned-close"
                onClick={() => { setReminderData(null); setReminderResult(null); setReminderError(null); }}
                className="px-3 py-2 border border-[var(--brand-stone)] text-xs uppercase tracking-[0.18em] hover:border-[var(--brand-forest)] transition-colors"
              >
                Close
              </button>
              <button
                data-testid="orders-abandoned-send"
                onClick={sendReminders}
                disabled={reminderBusy || reminderData.count === 0}
                className="px-4 py-2 bg-[var(--brand-gold)] text-[var(--brand-forest-deep)] font-semibold text-xs uppercase tracking-[0.18em] disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                {reminderBusy ? 'Sending…' : `Send ${reminderData.count} reminder${reminderData.count === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>

          {reminderError && (
            <div data-testid="orders-abandoned-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 mb-3">
              {reminderError}
            </div>
          )}
          {reminderResult && (
            <div data-testid="orders-abandoned-result" className="text-sm bg-[var(--brand-olive)]/10 border border-[var(--brand-olive)]/30 text-[var(--brand-forest)] px-3 py-2 mb-3">
              Sent <strong>{reminderResult.sent}</strong> of {reminderResult.attempted}.
              {reminderResult.skipped_no_resend_config > 0 && (
                <> ({reminderResult.skipped_no_resend_config} skipped — Resend not configured)</>
              )}
              {reminderResult.errors && reminderResult.errors.length > 0 && (
                <> ({reminderResult.errors.length} errors)</>
              )}
            </div>
          )}

          {reminderData.count === 0 ? (
            <div data-testid="orders-abandoned-empty" className="text-sm text-[var(--brand-muted)] italic">
              Nothing to chase up. Every started checkout is either paid or too fresh (&lt;24h).
            </div>
          ) : (
            <ul data-testid="orders-abandoned-list" className="space-y-2">
              {reminderData.candidates.map((c) => (
                <li
                  key={c.session_id}
                  data-testid={`orders-abandoned-row-${c.session_id}`}
                  className="flex items-center justify-between gap-3 bg-white border border-[var(--brand-stone)] px-3 py-2 text-xs"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[var(--brand-forest)] truncate">{c.product_name}</div>
                    <div className="text-[var(--brand-muted)] truncate">{c.customer_email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-[var(--brand-olive)]">£{Number(c.amount || 0).toFixed(2)}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-muted)]">
                      {fmtDateTime(c.created_at)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && (
        <div data-testid="orders-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">
          {error}
        </div>
      )}

      {!filtered.length ? (
        <EmptyState
          icon={Package}
          title={rows.length ? 'Nothing in this view' : 'No orders yet'}
          body={
            rows.length
              ? 'Switch the filter above to see other states.'
              : 'When a customer pays via Stripe, their order will land here. You\u2019ll then forward the details to Eahora and confirm dispatch.'
          }
        />
      ) : (
        <ul data-testid="orders-list" className="space-y-3">
          {filtered.map((o) => {
            const status = o.fulfilment_status || 'paid';
            const state = ORDER_STATES[status] || ORDER_STATES.paid;
            const isBusy = busyId === o.id;
            return (
              <li
                key={o.id}
                data-testid={`order-row-${o.id}`}
                className="bg-white border border-[var(--brand-stone)] p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`uppercase tracking-[0.18em] px-2 py-0.5 ${state.badgeClass}`}>
                        {state.label}
                      </span>
                      {o.age_restricted && (
                        <span
                          data-testid={`order-age-badge-${o.id}`}
                          title={o.age_confirmed ? 'Buyer confirmed 18+ before payment' : 'Age confirmation missing'}
                          className="uppercase tracking-[0.18em] px-2 py-0.5 bg-[var(--brand-forest-deep)] text-[var(--brand-gold)]"
                        >
                          {o.age_confirmed ? '18+ Verified' : '18+ Required'}
                        </span>
                      )}
                      <span className="text-[var(--brand-muted)]">{fmtDateTime(o.created_at)}</span>
                    </div>
                    <div className="mt-2 font-display text-xl text-[var(--brand-forest)]">
                      {o.product_name}
                    </div>
                    <div className="mt-1 text-sm text-[var(--brand-ink)]">
                      {o.customer_email ? (
                        <a
                          href={`mailto:${o.customer_email}`}
                          data-testid={`order-email-${o.id}`}
                          className="link-expand text-[var(--brand-olive)] font-semibold"
                        >
                          {o.customer_email}
                        </a>
                      ) : (
                        <span className="text-[var(--brand-muted)] italic">no email captured</span>
                      )}
                      <span className="text-[var(--brand-muted)]"> · SKU </span>
                      <code className="text-xs bg-[var(--brand-parchment)] px-1.5 py-0.5">{o.slug}</code>
                      <span className="text-[var(--brand-muted)]"> · </span>
                      <strong className="text-[var(--brand-forest)]">{fmtGBP(o.amount)}</strong>
                    </div>
                    {o.fulfilment_note && (
                      <p className="mt-2 text-xs text-[var(--brand-muted)] italic">
                        Note: {o.fulfilment_note}
                      </p>
                    )}

                    {Array.isArray(o.bundle_members) && o.bundle_members.length > 0 && (
                      <div
                        data-testid={`order-bundle-members-${o.id}`}
                        className="mt-3 bg-[var(--brand-parchment)] border-l-4 border-l-[var(--brand-gold)] px-3 py-2"
                      >
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)] mb-1.5">
                          Bundle · Pack {o.bundle_members.length} SKUs
                        </div>
                        <ul className="space-y-1 text-xs text-[var(--brand-ink)]">
                          {o.bundle_members.map((m) => (
                            <li key={m.slug} className="flex items-baseline gap-2">
                              <span className="w-1 h-1 bg-[var(--brand-gold)] shrink-0" />
                              {m.sku && (
                                <code className="text-[10px] bg-[var(--brand-parchment-warm)] px-1.5 py-0.5 font-semibold shrink-0">
                                  {m.sku}
                                </code>
                              )}
                              <span className="font-semibold">{m.name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <ShippingBlock order={o} />

                    <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[var(--brand-muted)]">
                      Session <code className="bg-[var(--brand-parchment)] px-1 normal-case tracking-normal">{o.session_id?.slice(0, 14)}…</code>
                      {o.forwarded_at && <> · Forwarded {fmtDateTime(o.forwarded_at)}</>}
                      {o.dispatched_at && <> · Dispatched {fmtDateTime(o.dispatched_at)}</>}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                    {status === 'paid' && (
                      <button
                        data-testid={`order-forward-${o.id}`}
                        disabled={isBusy}
                        onClick={() => {
                          const note = window.prompt(
                            'Optional note for this forward (e.g. Eahora order ref, courier picked):',
                            '',
                          );
                          if (note === null) return; // user cancelled
                          action(o.id, () => adminApi.markOrderForwarded(o.id, note || null));
                        }}
                        className="btn-gold inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-none disabled:opacity-60"
                      >
                        {isBusy ? <Loader2 size={13} className="animate-spin" /> : <Truck size={13} />}
                        Mark as forwarded
                      </button>
                    )}
                    {status === 'forwarded' && (
                      <button
                        data-testid={`order-dispatched-${o.id}`}
                        disabled={isBusy}
                        onClick={() => {
                          const note = window.prompt(
                            'Optional tracking number or note from Eahora:',
                            '',
                          );
                          if (note === null) return;
                          action(o.id, () => adminApi.markOrderDispatched(o.id, note || null));
                        }}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-semibold border border-[var(--brand-forest)] text-[var(--brand-forest)] hover:bg-[var(--brand-forest)] hover:text-[var(--brand-parchment)] transition-colors disabled:opacity-60"
                      >
                        {isBusy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                        Mark as dispatched
                      </button>
                    )}
                    {status !== 'paid' && (
                      <button
                        data-testid={`order-reopen-${o.id}`}
                        disabled={isBusy}
                        onClick={() => action(o.id, () => adminApi.reopenOrder(o.id))}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs border border-[var(--brand-stone)] text-[var(--brand-muted)] hover:border-red-400 hover:text-red-600 transition-colors disabled:opacity-60"
                        title="Roll back to 'Awaiting forward'"
                      >
                        <Undo2 size={12} /> Reopen
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Subscribers
// ──────────────────────────────────────────────────────────────
function WishlistPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/wishlist/summary?limit=25`)
      .then((r) => setData(r.data))
      .catch(() => setError('Could not load wishlist summary'));
  }, []);

  if (error) {
    return <div data-testid="wishlist-summary-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">{error}</div>;
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--brand-muted)]">
        <Loader2 size={14} className="animate-spin" /> Loading wishlist demand…
      </div>
    );
  }

  const items = data.items || [];

  return (
    <div data-testid="wishlist-summary-panel">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl text-[var(--brand-forest)]">Most-Wished-For</h2>
          <p className="text-xs text-[var(--brand-muted)] mt-1">
            Anonymous demand signal — customers heart-saving products before enquiring or buying. Total events tracked:{' '}
            <strong>{data.total_events || 0}</strong>
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-[var(--brand-muted)] italic border border-dashed border-[var(--brand-stone)] px-4 py-6 text-center">
          Nothing saved yet. Once visitors start heart-saving products, they&rsquo;ll show up here so you can spot demand before Barry&rsquo;s reply lands.
        </div>
      ) : (
        <div className="border border-[var(--brand-stone)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--brand-parchment-warm)] text-left text-[10px] uppercase tracking-[0.18em] text-[var(--brand-muted)]">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3 text-right">Net saves</th>
                <th className="px-4 py-3 text-right">Adds</th>
                <th className="px-4 py-3 text-right">Removes</th>
                <th className="px-4 py-3">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, i) => (
                <tr
                  key={row.slug}
                  data-testid={`wishlist-summary-row-${row.slug}`}
                  className="border-t border-[var(--brand-stone)]"
                >
                  <td className="px-4 py-3 text-xs text-[var(--brand-muted)]">{i + 1}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[var(--brand-forest)]">{row.product_name || row.slug}</div>
                    {row.product_price && (
                      <div className="text-xs text-[var(--brand-muted)]">{row.product_price}</div>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-muted)] mt-1">{row.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-[var(--brand-gold)] tabular-nums">{row.net_saves}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[var(--brand-forest)]">{row.adds}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[var(--brand-muted)]">{row.removes}</td>
                  <td className="px-4 py-3 text-xs text-[var(--brand-muted)]">{fmtDateTime(row.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Bundles — conversion analytics for HERITAGE_BUNDLES SKUs
// ──────────────────────────────────────────────────────────────
function BundlesPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/bundle-stats`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load bundle stats'));
  }, []);

  if (error) {
    return (
      <div data-testid="bundles-panel-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">
        {error}
      </div>
    );
  }
  if (!data) return <PanelLoader />;

  const { bundles = [], totals = {} } = data;

  return (
    <div data-testid="bundles-panel" className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-eyebrow mb-1">Curated Bundles</div>
          <h2 className="font-display text-2xl text-[var(--brand-forest)] tracking-tight">
            Bundle conversion &amp; revenue
          </h2>
          <p className="mt-1 text-xs text-[var(--brand-muted)]">
            Live data from Stripe checkout sessions. Paid status confirms once the
            webhook lands. Tune price / promotion after the first four weeks.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 sm:gap-5">
          <StatTile testId="bundles-total-initiated" label="Sessions" value={totals.initiated_count || 0} />
          <StatTile testId="bundles-total-paid" label="Paid" value={totals.paid_count || 0} />
          <StatTile testId="bundles-total-revenue" label="Revenue" value={fmtGBP(totals.revenue_gbp || 0)} />
        </div>
      </div>

      {bundles.length === 0 ? (
        <div data-testid="bundles-panel-empty" className="bg-[var(--brand-parchment-warm)] border border-[var(--brand-stone)] px-6 py-10 text-center">
          <ShoppingBag size={22} className="mx-auto text-[var(--brand-muted)] mb-3" />
          <div className="font-display text-lg text-[var(--brand-forest)]">
            No bundle sessions yet.
          </div>
          <p className="mt-1 text-sm text-[var(--brand-muted)]">
            Once a customer clicks Buy on the Highland Fire Kit, Bothy Weekend Kit,
            Deep Winter Kit or The Starter, the row will appear here.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[var(--brand-stone)]">
          <table className="w-full text-sm">
            <thead className="bg-[var(--brand-parchment-warm)] text-[var(--brand-forest)] text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-[0.16em]">Bundle</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-[0.16em] text-right">Price</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-[0.16em] text-right">Sessions</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-[0.16em] text-right">Paid</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-[0.16em] text-right">Conv.</th>
                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-[0.16em] text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="bg-[var(--brand-parchment)]">
              {bundles.map((b) => (
                <tr
                  key={b.slug}
                  data-testid={`bundles-row-${b.slug}`}
                  className="border-t border-[var(--brand-stone)]"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[var(--brand-forest)]">{b.product_name}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--brand-muted)] font-semibold mt-0.5">
                      {b.slug}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">£{Number(b.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{b.initiated_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{b.paid_count}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className={
                      'px-2 py-0.5 text-xs font-semibold ' +
                      (b.conversion_rate >= 20
                        ? 'bg-[var(--brand-olive)]/15 text-[var(--brand-forest)]'
                        : b.conversion_rate >= 5
                          ? 'bg-[var(--brand-gold)]/20 text-[var(--brand-forest-deep)]'
                          : 'bg-[var(--brand-stone)] text-[var(--brand-muted)]')
                    }>
                      {b.conversion_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-[var(--brand-olive)]">
                    {fmtGBP(b.revenue_gbp || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-[var(--brand-parchment-warm)] border border-[var(--brand-stone)] px-4 py-3 min-w-[100px]">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--brand-muted)] font-semibold">
        {label}
      </div>
      <div className="mt-1 font-display text-xl text-[var(--brand-forest)] tabular-nums">
        {value}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Subscribers
// ──────────────────────────────────────────────────────────────
function SubscribersPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { adminApi.subscribers().then(setRows).catch(() => setRows([])); }, []);

  const downloadCsv = () => {
    if (!rows?.length) return;
    const csv = ['email,source,created_at']
      .concat(rows.map((r) => `"${r.email}","${r.source || ''}","${r.created_at}"`))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ecobike-subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (rows === null) return <PanelLoader />;
  if (!rows.length) {
    return <EmptyState icon={Users} title="No subscribers yet"
      body="Field Notes signups appear here. A simple CSV export will be available once you have a few." />;
  }
  return (
    <div data-testid="subscribers-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-3xl text-[var(--brand-forest)]">{rows.length} subscribers</h2>
        <button
          data-testid="subscribers-export-csv"
          onClick={downloadCsv}
          className="btn-gold px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] rounded-none"
        >
          Export CSV
        </button>
      </div>
      <div className="bg-white border border-[var(--brand-stone)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--brand-parchment-warm)] text-xs uppercase tracking-[0.18em] text-[var(--brand-forest)]">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Joined</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || `${r.email}-${i}`} className={i % 2 ? 'bg-[var(--brand-parchment)]/50' : ''}>
                <td className="px-4 py-3 font-semibold text-[var(--brand-forest)]">{r.email}</td>
                <td className="px-4 py-3 text-[var(--brand-muted)]">{r.source}</td>
                <td className="px-4 py-3 text-[var(--brand-muted)]">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Sales Forecast
// ──────────────────────────────────────────────────────────────
const STORAGE_KEY = 'ecobike_forecast_v1';
const FORECAST_DEFAULTS = {
  bikeWholesale: 1450,
  bikeRetail: 1959,
  campingWholesale: 110,
  campingRetail: 159,
  monthlyBikes: 5,
  monthlyCampingItems: 25,
  adSpendMonthly: 450,
  fixedMonthlyCosts: 200,
};

function ForecastNumberField({ k, label, prefix, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[var(--brand-olive)] font-semibold mb-1.5">{label}</span>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-muted)] text-sm">{prefix}</span>}
        <input
          data-testid={`forecast-${k}`}
          type="number"
          inputMode="numeric"
          value={value}
          onChange={onChange}
          className={`field ${prefix ? 'pl-7' : ''}`}
        />
      </div>
    </label>
  );
}

function ForecastPanel() {
  const [v, setV] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      return { ...FORECAST_DEFAULTS, ...(stored || {}) };
    } catch { return FORECAST_DEFAULTS; }
  });
  const [saved, setSaved] = useState(false);

  const set = (k) => (e) => {
    const next = { ...v, [k]: Number(e.target.value) || 0 };
    setV(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    setSaved(true);
    setTimeout(() => setSaved(false), 1200);
  };

  const f = useMemo(() => {
    const bikeMargin = v.bikeRetail - v.bikeWholesale;
    const campingMargin = v.campingRetail - v.campingWholesale;
    const revenue = v.bikeRetail * v.monthlyBikes + v.campingRetail * v.monthlyCampingItems;
    const grossProfit = bikeMargin * v.monthlyBikes + campingMargin * v.monthlyCampingItems;
    const netProfit = grossProfit - v.adSpendMonthly - v.fixedMonthlyCosts;
    const annualRevenue = revenue * 12;
    const annualNet = netProfit * 12;
    const bikeMarginPct = v.bikeRetail ? Math.round((bikeMargin / v.bikeRetail) * 100) : 0;
    const breakEvenBikes = bikeMargin > 0
      ? Math.ceil((v.adSpendMonthly + v.fixedMonthlyCosts) / bikeMargin)
      : 0;
    return { revenue, grossProfit, netProfit, annualRevenue, annualNet, bikeMargin, campingMargin, bikeMarginPct, breakEvenBikes };
  }, [v]);

  return (
    <div data-testid="forecast-panel" className="grid lg:grid-cols-12 gap-8">
      <section className="lg:col-span-7 space-y-8">
        <div>
          <h2 className="font-display text-3xl text-[var(--brand-forest)] mb-1">Sales forecast</h2>
          <p className="text-[var(--brand-muted)] text-sm">Plug in real numbers; the projections update instantly. Saved locally in your browser.</p>
        </div>

        <div className="bg-white border border-[var(--brand-stone)] p-6 lg:p-8 space-y-6">
          <h3 className="font-display text-xl text-[var(--brand-forest)]">Per-unit economics</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <ForecastNumberField k="bikeWholesale" label="E-bike — Eahora wholesale" prefix="£" value={v.bikeWholesale} onChange={set('bikeWholesale')} />
            <ForecastNumberField k="bikeRetail" label="E-bike — your retail" prefix="£" value={v.bikeRetail} onChange={set('bikeRetail')} />
            <ForecastNumberField k="campingWholesale" label="Camping item — wholesale (avg)" prefix="£" value={v.campingWholesale} onChange={set('campingWholesale')} />
            <ForecastNumberField k="campingRetail" label="Camping item — retail (avg)" prefix="£" value={v.campingRetail} onChange={set('campingRetail')} />
          </div>
        </div>

        <div className="bg-white border border-[var(--brand-stone)] p-6 lg:p-8 space-y-6">
          <h3 className="font-display text-xl text-[var(--brand-forest)]">Monthly volume & spend</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <ForecastNumberField k="monthlyBikes" label="Bikes sold per month" value={v.monthlyBikes} onChange={set('monthlyBikes')} />
            <ForecastNumberField k="monthlyCampingItems" label="Camping items sold per month" value={v.monthlyCampingItems} onChange={set('monthlyCampingItems')} />
            <ForecastNumberField k="adSpendMonthly" label="Marketing spend per month" prefix="£" value={v.adSpendMonthly} onChange={set('adSpendMonthly')} />
            <ForecastNumberField k="fixedMonthlyCosts" label="Other fixed costs per month" prefix="£" value={v.fixedMonthlyCosts} onChange={set('fixedMonthlyCosts')} />
          </div>
        </div>

        {saved && (
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--brand-olive)]">
            <CheckCircle2 size={14} /> Saved
          </div>
        )}
      </section>

      <aside className="lg:col-span-5 space-y-4 lg:sticky lg:top-6 lg:self-start">
        <Tile testId="forecast-revenue" label="Projected monthly revenue" value={fmtGBP(f.revenue)} accent />
        <Tile testId="forecast-gross" label="Gross profit / month" value={fmtGBP(f.grossProfit)} />
        <Tile testId="forecast-net" label="Net profit / month (after marketing & fixed costs)" value={fmtGBP(f.netProfit)} highlight={f.netProfit >= 0} />
        <div className="grid grid-cols-2 gap-3">
          <Tile testId="forecast-annual-revenue" label="Year 1 revenue" value={fmtGBP(f.annualRevenue)} compact />
          <Tile testId="forecast-annual-net" label="Year 1 net profit" value={fmtGBP(f.annualNet)} compact highlight={f.annualNet >= 0} />
        </div>
        <div className="bg-[var(--brand-forest)] text-[var(--brand-parchment)] p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--brand-gold)] mb-2">Quick reads</div>
          <ul className="space-y-1 text-sm">
            <li><span className="opacity-80">E-bike gross margin:</span> <strong className="text-[var(--brand-gold)]">{fmtGBP(f.bikeMargin)} ({f.bikeMarginPct}%)</strong></li>
            <li><span className="opacity-80">Break-even bikes / month:</span> <strong className="text-[var(--brand-gold)]">{f.breakEvenBikes}</strong></li>
          </ul>
        </div>
      </aside>
    </div>
  );
}

function Tile({ label, value, accent, highlight, compact, testId }) {
  return (
    <div
      data-testid={testId}
      className={`p-5 border ${accent ? 'bg-[var(--brand-forest)] text-[var(--brand-parchment)] border-[var(--brand-forest)]' : 'bg-white border-[var(--brand-stone)]'}`}
    >
      <div className={`text-[10px] uppercase tracking-[0.2em] ${accent ? 'text-[var(--brand-gold)]' : 'text-[var(--brand-olive)]'} font-semibold`}>{label}</div>
      <div className={`mt-1 font-display ${compact ? 'text-2xl' : 'text-3xl lg:text-4xl'} ${highlight === false ? 'text-red-700' : accent ? '' : 'text-[var(--brand-forest)]'}`}>
        {value}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Marketing tracker
// ──────────────────────────────────────────────────────────────
function MarketingPanel() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ channel: '', action: '', note: '', cost_gbp: '', enquiries_attributed: '' });
  const [submitting, setSubmitting] = useState(false);

  const reload = () => adminApi.marketingEvents().then(setRows).catch(() => setRows([]));
  useEffect(() => { reload(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.channel || !form.action) return;
    setSubmitting(true);
    try {
      await adminApi.createMarketingEvent({
        channel: form.channel,
        action: form.action,
        note: form.note || null,
        cost_gbp: form.cost_gbp ? Number(form.cost_gbp) : 0,
        enquiries_attributed: form.enquiries_attributed ? Number(form.enquiries_attributed) : 0,
      });
      setForm({ channel: '', action: '', note: '', cost_gbp: '', enquiries_attributed: '' });
      reload();
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    await adminApi.deleteMarketingEvent(id);
    reload();
  };

  const totals = useMemo(() => {
    if (!rows?.length) return { cost: 0, enq: 0 };
    return rows.reduce((acc, r) => ({
      cost: acc.cost + (r.cost_gbp || 0),
      enq: acc.enq + (r.enquiries_attributed || 0),
    }), { cost: 0, enq: 0 });
  }, [rows]);

  return (
    <div data-testid="marketing-panel" className="grid lg:grid-cols-12 gap-8">
      <section className="lg:col-span-5">
        <h2 className="font-display text-3xl text-[var(--brand-forest)] mb-1">Marketing tracker</h2>
        <p className="text-[var(--brand-muted)] text-sm mb-6">Log every press pitch, partnership outreach, ad campaign, event — and what it drove.</p>
        <form onSubmit={submit} className="bg-white border border-[var(--brand-stone)] p-6 space-y-4">
          <Field label="Channel *" testId="marketing-channel">
            <input className="field" required value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              placeholder="Sunday Post, Meta Ads, Cycling UK…" />
          </Field>
          <Field label="Action *" testId="marketing-action">
            <input className="field" required value={form.action}
              onChange={(e) => setForm({ ...form, action: e.target.value })}
              placeholder="Sent pitch, started £15/day campaign…" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Cost (£)" testId="marketing-cost">
              <input className="field" type="number" min="0" step="0.01" value={form.cost_gbp}
                onChange={(e) => setForm({ ...form, cost_gbp: e.target.value })} />
            </Field>
            <Field label="Enquiries driven" testId="marketing-enq">
              <input className="field" type="number" min="0" value={form.enquiries_attributed}
                onChange={(e) => setForm({ ...form, enquiries_attributed: e.target.value })} />
            </Field>
          </div>
          <Field label="Notes" testId="marketing-note">
            <textarea className="field" rows={3} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Who you sent it to, follow-up date, link…" />
          </Field>
          <button
            data-testid="marketing-add"
            type="submit"
            disabled={submitting}
            className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-none disabled:opacity-60"
          >
            <Plus size={14} /> {submitting ? 'Saving…' : 'Log event'}
          </button>
        </form>
      </section>

      <section className="lg:col-span-7">
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Tile testId="marketing-total-cost" label="Total marketing spend" value={fmtGBP(totals.cost)} compact />
          <Tile testId="marketing-total-enq" label="Total attributed enquiries" value={String(totals.enq)} compact accent />
        </div>
        {rows === null ? <PanelLoader /> : !rows.length ? (
          <EmptyState icon={Megaphone} title="Nothing logged yet" body="Log your first press pitch, ad campaign, or partnership outreach on the left." />
        ) : (
          <ul data-testid="marketing-list" className="space-y-3">
            {rows.map((r) => (
              <li key={r.id} className="bg-white border border-[var(--brand-stone)] p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--brand-muted)]">
                      <span className="bg-[var(--brand-forest)] text-[var(--brand-parchment)] px-2 py-0.5">{r.channel}</span>
                      <span>{fmtDate(r.created_at)}</span>
                    </div>
                    <div className="mt-2 font-display text-lg text-[var(--brand-forest)]">{r.action}</div>
                    {r.note && <p className="mt-1 text-sm text-[var(--brand-ink)] whitespace-pre-wrap">{r.note}</p>}
                  </div>
                  <button
                    onClick={() => remove(r.id)}
                    aria-label="Delete event"
                    className="w-8 h-8 grid place-items-center border border-[var(--brand-stone)] hover:border-red-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-[var(--brand-muted)]">
                  <span>Cost: <strong className="text-[var(--brand-forest)]">{fmtGBP(r.cost_gbp)}</strong></span>
                  <span>Enquiries: <strong className="text-[var(--brand-forest)]">{r.enquiries_attributed}</strong></span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Field({ label, testId, children }) {
  return (
    <label className="block" data-testid={`${testId}-field`}>
      <span className="block text-xs uppercase tracking-[0.18em] text-[var(--brand-olive)] font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}
